import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { buttonClasses, filterClasses } from "../common/tailwindClassnames";
import SelectInput from "../common/SelectInput";

type ConnectionCandidate = {
    id: string;
    provider?: { name?: string; type?: { name?: string } };
    owner?: { id: string; name: string };
    destinations?: { id: string }[];
};
type FilterOption = { label: string; value: string };

type AddToConnectionDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    candidates: ConnectionCandidate[];
    loading: boolean;
    // Attach this record to the chosen connection; the parent owns the PATCH.
    onAttach: (connection: ConnectionCandidate) => Promise<void> | void;
    onSearch: (searchStr: string) => void;
    hasMore: boolean;
    workspaceOptions: FilterOption[];
    workspaceFilter: FilterOption | null;
    onChangeWorkspaceFilter: (option: FilterOption | null) => void;
    loadingWorkspaces?: boolean;
};

// Mirror of the flow map's AddDestinationDrawer, pointed the other way: instead of
// picking destinations for a connection, pick a connection for this record. Radio
// selection — a record joins one connection per confirm.
const AddToConnectionDrawer = ({
    isOpen, onClose, candidates, loading, onAttach, onSearch, hasMore,
    workspaceOptions, workspaceFilter, onChangeWorkspaceFilter, loadingWorkspaces,
}: AddToConnectionDrawerProps) => {
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState("");
    // Only queries the user actually typed (debounced) go back up as a reload.
    const lastSent = useRef<string | null>(null);

    useEffect(() => {
        if (!isOpen) { setSelectedId(null); setSaving(false); setQuery(""); lastSent.current = null; }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (lastSent.current === null) { lastSent.current = query; return; }
        if (query === lastSent.current) return;
        const timer = setTimeout(() => { lastSent.current = query; onSearch(query); }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, isOpen]);

    const selected = candidates.find((c) => c.id === selectedId);

    return (
        <div
            data-testid="AddToConnectionDrawer-panel"
            className={`fixed top-0 right-0 h-full w-full md:w-[420px] bg-white border-l border-gray-lines shadow-xl z-[60] transition-transform duration-300 flex flex-col ${
                isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"}`}
            aria-hidden={!isOpen}
            {...({ inert: isOpen ? undefined : "" } as any)}>
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-lines">
                <h2 className="text-lg font-semibold text-off-black">{t("Add to a connection")}</h2>
                <button onClick={onClose} aria-label={t("Close")}
                    className="w-9 h-9 flex items-center justify-center text-medium-gray hover:text-off-black">✕</button>
            </div>

            <div className="px-4 pt-4 flex flex-col gap-3">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Search connections")}
                    className="w-full border border-field-outline rounded px-3 py-2 text-sm text-off-black placeholder:text-secondary-text focus:outline-none focus:border-connection-green"
                />
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="drawer_connection_workspace"
                        options={workspaceOptions}
                        value={workspaceFilter}
                        onChange={onChangeWorkspaceFilter}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={!!loadingWorkspaces}
                        className=""
                        variant="connectionFilter"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
                {loading
                    ? [0, 1, 2, 3].map((i) => <div key={i} className="h-10 w-full bg-neutral-gray-50 rounded animate-pulse mb-2" />)
                    : candidates.length === 0
                        ? <p className="text-sm text-secondary-text mb-4">
                            {query ? t("No connections match your search.")
                                : workspaceFilter ? t("No eligible connections in this workspace.")
                                    : t("No eligible connections.")}
                          </p>
                        : candidates.map((c) => (
                            <label key={c.id} className="flex items-start gap-3 py-2 border-b border-gray-lines cursor-pointer">
                                <input type="radio" name="target-connection" className="mt-1 accent-connection-green"
                                    checked={selectedId === c.id}
                                    onChange={() => setSelectedId(c.id)} />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm text-off-black truncate">{c.provider?.name || c.id}</span>
                                    {c.owner?.name && (
                                        <span className="block text-xs text-secondary-text truncate">{c.owner.name}</span>
                                    )}
                                </span>
                                {c.provider?.type?.name && (
                                    <span className="flex-none bg-light-green text-connection-green rounded-full px-2 py-0.5 text-[11px] font-semibold">
                                        {c.provider.type.name}
                                    </span>
                                )}
                            </label>
                        ))}
                {!loading && hasMore && (
                    <p className="text-xs text-secondary-text mt-2">
                        {t("Showing the first matches only — refine your search to find more.")}
                    </p>
                )}
            </div>

            <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-lines">
                <button className={buttonClasses.primary} disabled={!selected || saving}
                    onClick={async () => {
                        if (!selected) return;
                        setSaving(true);
                        try { await onAttach(selected); } finally { setSaving(false); }
                    }}>
                    {t("Add to connection")}
                </button>
                <button className={buttonClasses.cancel} onClick={onClose}>{t("Cancel")}</button>
            </div>
        </div>
    );
};

export default AddToConnectionDrawer;
